/**
 * UpdateNotification - уведомление о доступном обновлении приложения
 * Показывается когда Service Worker обнаружил новую версию
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './UpdateNotification.module.css';

interface UpdateNotificationProps {
  onUpdate: () => void;
  onDismiss: () => void;
}

export function UpdateNotification({ onUpdate, onDismiss }: UpdateNotificationProps) {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(true);

  const handleUpdate = () => {
    onUpdate();
    setIsVisible(false);
  };

  const handleDismiss = () => {
    onDismiss();
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className={styles.notification}>
      <div className={styles.content}>
        <span className={styles.icon}>🔄</span>
        <div className={styles.text}>
          <strong>{t('notification.update.title')}</strong>
          <span>{t('notification.update.description')}</span>
        </div>
      </div>
      <div className={styles.actions}>
        <button className={styles.dismissButton} onClick={handleDismiss}>
          {t('btn.later')}
        </button>
        <button className={styles.updateButton} onClick={handleUpdate}>
          {t('btn.update')}
        </button>
      </div>
    </div>
  );
}

/**
 * Хук для управления уведомлением об обновлении
 */
export function useUpdateNotification() {
  const [showUpdate, setShowUpdate] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleControllerChange = () => {
      // Перезагружаем страницу когда новый SW взял контроль
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    // Проверяем есть ли ожидающий воркер при загрузке
    navigator.serviceWorker.ready.then((registration) => {
      if (registration.waiting) {
        setWaitingWorker(registration.waiting);
        setShowUpdate(true);
      }

      // Слушаем обновления
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            setWaitingWorker(newWorker);
            setShowUpdate(true);
          }
        });
      });
    });

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  const handleUpdate = () => {
    if (waitingWorker) {
      waitingWorker.postMessage('skipWaiting');
    }
    setShowUpdate(false);
  };

  const handleDismiss = () => {
    setShowUpdate(false);
  };

  return {
    showUpdate,
    handleUpdate,
    handleDismiss,
  };
}

export default UpdateNotification;

